import SashiDo from 'lib/SashiDo';
import AccountManager from 'lib/AccountManager';
import * as AJAX from 'lib/AJAX';
import encodeFormData from 'lib/encodeFormData';
import Parse from 'parse';

export default class ParseApp {
  constructor({ serverInfo, ...args }) {
    this.__constructor(args);

    this.settings = {
      fields: {},
      lastFetched: new Date(0),
    };

    this.latestRelease = {
      release: null,
      lastFetched: new Date(0),
    };

    this.jobStatus = {
      status: null,
      lastFetched: new Date(0),
    };

    this.classCounts = {
      counts: {},
      lastFetched: {},
    };

    this.serverInfo = serverInfo || {};
    this.hasCheckedForMigraton = false;
  }

  __constructor({
    objectId,
    region,
    user,
    name,
    slug,
    icon,
    appRequests,
    createdAt,
    clientKey,
    appId,
    parseServerVersion,
    serverUrl,
    graphQLServerURL,
    databaseUri,
    javascriptKey,
    masterKey,
    restAPIKey,
    dotNetKey,
    webhookKey,
    fileKey,
    serviceName,
    production,
    stats,
    server_status,
    migration_status,
    mountPath,
    disable_engine_feature,
    supportedPushLocales,

    hidden,

    ...params
  }) {
    this.id = objectId;
    this.region = region;
    this.user = user;
    this.parseServerVersion = parseServerVersion;
    this.name = name;
    this.icon = icon ? icon.url : null;
    this.appRequests = appRequests;
    this.createdAt = createdAt ? new Date(createdAt) : new Date();
    this.applicationId = appId;
    this.clientKey = clientKey;
    this.javascriptKey = javascriptKey;
    this.masterKey = masterKey;
    this.restKey = restAPIKey;
    this.dotNetKey = dotNetKey;
    this.webhookKey = webhookKey;
    this.fileKey = fileKey;
    this.serviceName = serviceName;
    this.production = production;
    this.serverURL = serverUrl;
    this.databaseUri = databaseUri;
    this.server_status = server_status || {};
    this.migration_status = migration_status || {};
    this.mountPath = mountPath;
    this.disable_engine_feature = disable_engine_feature;

    this.supportedPushLocales = supportedPushLocales ? supportedPushLocales : [];

    this.params = params;
    this.stats = stats || {};

    this.slug = `${objectId}-${slug}`;

    this.serverURL = mountPath ? this.serverURL.replace('/1/', mountPath) : this.serverURL;

    const version = this.parseServerVersion.replace(/[^0-9\.]+/g, '').split('.');
    const cVersion = Number((`${version[0]}.${version[1]}`).replace(/[^0-9\.]+/g, ''));
    if (cVersion >= 3.5 || this.graphQLServerURL) {
      this.graphQLServerURL = this.serverURL.replace('/1', '/graphql');
    }
  }

  reload() {
    return SashiDo.getApp({ objectId: this.id });
  }

  setParseKeys() {
    Parse.serverURL = this.serverURL;
    Parse._initialize(this.applicationId, this.javascriptKey, this.masterKey);

    if (this.params.allowCustomUserClass) {
      Parse.User.allowCustomUserClass(this.params.allowCustomUserClass);
    }
  }

  apiRequest(method, path, params, options) {
    this.setParseKeys();
    return Parse._request(method, path, params, options);
  }

  getServerInfo() {
    return this.apiRequest('GET', 'serverInfo', {}, { useMasterKey: true });
  }

  /**
   * lines - maximum number of lines to fetch
   * since - only fetch lines since this Date
   */
  getLogs(level, since) {
    const path = `scriptlog?level=${encodeURIComponent(level.toLowerCase())}&n=100${since ? `&startDate=${encodeURIComponent(since.getTime())}` : ''}`;
    return this.apiRequest('GET', path, {}, { useMasterKey: true });
  }

  /**
   * Fetches source of a Cloud Code hosted file from api.parse.com
   * fileName - the name of the file to be fetched
   */
  getSource(fileName) {
    return this.getLatestRelease().then((release) => {
      if (release.files === null) {
        // No release yet
        return Promise.resolve(null);
      }

      let fileMetaData = release.files[fileName];
      if (fileMetaData && fileMetaData.source) {
        return Promise.resolve(fileMetaData.source);
      }

      let params = {
        version: fileMetaData.version,
        checksum: fileMetaData.checksum
      }
      return this.apiRequest('GET', `scripts/${fileName}`, params, { useMasterKey: true });
    }).then((source) => {
      if (this.latestRelease.files) {
        this.latestRelease.files[fileName].source = source;
      }

      return Promise.resolve(source);
    });
  }

  getLatestRelease() {
    if (new Date() - this.latestRelease.lastFetched < 60000) {
      return Promise.resolve(this.latestRelease);
    }
    return this.apiRequest(
      'GET',
      'releases/latest',
      {},
      { useMasterKey: true }
    ).then((release) => {
      this.latestRelease.lastFetched = new Date();
      this.latestRelease.files = null;

      const latestRelease = Array.isArray(release) ? release[0] : release;


      this.latestRelease.release = {
        version: latestRelease.version,
        parseVersion: latestRelease.parseVersion,
        deployedAt: new Date(latestRelease.timestamp),
      };

      let checksums = JSON.parse(latestRelease.checksums);
      let versions = JSON.parse(latestRelease.userFiles);
      this.latestRelease.files = {};

      // The scripts can be in `/` or in `/cloud`. Let's check for both.
      if (checksums.cloud) {
        checksums = checksums.cloud;
      }

      if (versions.cloud) {
        versions = versions.cloud;
      }

      for (const c in checksums) {
        this.latestRelease.files[c] = {
          checksum: checksums[c],
          version: versions[c],
          source: null,
        };
      }

      return Promise.resolve(this.latestRelease);
    });
  }

  getClassCount(className) {
    this.setParseKeys();
    if (this.classCounts.counts[className] !== undefined) {
      // Cache it for a minute
      if (new Date() - this.classCounts.lastFetched[className] < 60000) {
        return Promise.resolve(this.classCounts.counts[className]);
      }
    }
    let p = new Parse.Query(className).count({ useMasterKey: true });
    p.then(count => {
      this.classCounts.counts[className] = count;
      this.classCounts.lastFetched[className] = new Date();
    })
    return p;
  }

  getRelationCount(relation) {
    this.setParseKeys();
    const p = relation.query().count({ useMasterKey: true });
    return p;
  }

  getAnalyticsRetention(time) {
    time = Math.round(time.getTime() / 1000);
    return AJAX.abortableGet('/apps/' + this.slug + '/analytics_retention?at=' + time);
  }

  getAnalyticsOverview(time) {
    time = Math.round(time.getTime() / 1000);
    let audiencePromises = [
      'daily_users',
      'weekly_users',
      'monthly_users',
      'total_users',
      'daily_installations',
      'weekly_installations',
      'monthly_installations',
      'total_installations'
    ].map((activity) => {
      let { xhr, promise } = AJAX.abortableGet('/apps/' + this.slug + '/analytics_content_audience?at=' + time + '&audienceType=' + activity);
      promise = promise.then((result) => (
        result.total === undefined ? result.content : result.total
      ));
      return { xhr, promise }
    });

    let billingPromises = [
      'billing_file_storage',
      'billing_database_storage',
      'billing_data_transfer'
    ].map((billing) => (
      AJAX.abortableGet('/apps/' + this.slug + '/' + billing)
    ));

    let allPromises = audiencePromises.concat(billingPromises);

    return {
      'dailyActiveUsers': allPromises[0],
      'weeklyActiveUsers': allPromises[1],
      'monthlyActiveUsers': allPromises[2],
      'totalUsers': allPromises[3],
      'dailyActiveInstallations': allPromises[4],
      'weeklyActiveInstallations': allPromises[5],
      'monthlyActiveInstallations': allPromises[6],
      'totalInstallations': allPromises[7],
      'billingFileStorage': allPromises[8],
      'billingDatabasetorage': allPromises[9],
      'billingDataTransfer': allPromises[10]
    };
  }

  getAnalyticsTimeSeries(query) {
    let path = '/apps/' + this.slug + '/analytics?' + encodeFormData(null, query);
    let { promise, xhr } = AJAX.abortableGet(path);
    promise = promise.then(({ requested_data }) => requested_data);
    return { promise, xhr };
  }

  getAnalyticsSlowQueries(className, os, version, from, to) {
    let path = '/apps/' + this.slug + '/slow_queries?' + encodeFormData(null, {
      className: className || '',
      os: os || '',
      version: version || '',
      from: from.getTime() / 1000,
      to: to.getTime() / 1000
    });
    let { promise, xhr } = AJAX.abortableGet(path);
    promise = promise.then(({ result }) => result);

    return { promise, xhr };
  }

  getAppleCerts() {
    return SashiDo.getAppleCerts({ id: this.id });
  }

  uploadAppleCert(props) {
    const { file, type, bundle, key_id, team_id } = props;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        SashiDo.uploadAppleCert({ id: this.id, file: reader.result.split(';base64,')[1], type, bundle, key_id, team_id }).then(({ cert, error }) => {
          if (cert) {
            resolve({ cert });
          }

          if (error) {
            resolve({ error });
          }
        });
      };
      reader.readAsDataURL(file);
    });
  }

  deleteAppleCert(id) {
    return SashiDo.deleteAppleCert({ id });
  }

  saveSettingsFields({ appName, fields }) {
    return SashiDo.setAppSettings({ id: this.id, fields }).then((data) => {
      SashiDo.activityTrack({
        type: 'update_app_settings',
        data: {
          message: `Application settings for app ${appName} was updated`,
        },
      });

      const newFields = { ...this.settings.fields, ...fields };

      return Promise.resolve({ success: true, failures: data.failures, successes: newFields });
    });
  }

  fetchSettingsFields() {
    // Cache it for a minute
    // pivanov - do not cache because of stupid Parse team shits!!!
    // if (new Date() - this.settings.lastFetched < 60000) {
    //   return Promise.resolve({ fields: this.settings.fields });
    // }

    return SashiDo.getAppSettings({ id: this.id }).then((response) => {
      // this.settings.fields = response.fields
      // This is for otherwise settings become a frozen object
      for (const f in response) {
        this.settings.fields[f] = response[f];
      }

      this.settings.lastFetched = new Date();
      return Promise.resolve(this.settings);
    });
  }

  resetMasterKey(password) {
    return SashiDo.resetMasterKey({ password, appObjectId: this.id }).then(({ new_key }) => {
      this.masterKey = new_key;
    });
  }

  clearCollection(className) {
    const path = `purge/${className}`;
    return this.apiRequest('DELETE', path, {}, { useMasterKey: true });
  }

  owner_collaborator() {
    return this.fetchSettingsFields().then(({ fields }) => {
      return AccountManager.currentUser().then(({ currentUser }) => {
        return (fields.collaborators.find(r => r.objectId === currentUser.id) || {});
      });
    });
  }

  fetchPushSubscriberCount = async (audienceId, query) => {
    let promise;
    if (audienceId === 'everyone') {
      query = {};
    }
    if (!query) {
      promise = new Parse.Query('_Audience').get(audienceId, { useMasterKey: true }).then(function (audience) {
        return Parse.Query.fromJSON('_Installation', { where: audience.get('query') }).count({ useMasterKey: true })
      });
    } else {
      promise = Parse.Query.fromJSON('_Installation', { where: query }).count({ useMasterKey: true })
    }

    let count = 0;

    try {
      count = await promise;
    } catch (e) {
      // console.log("@@@ ERROR", e.message)
    }

    return count;
  }

  fetchPushAudience(limit) {
    return this.apiRequest('GET', 'push_audience', { limit }, { useMasterKey: true });
  }

  createPushAudience(name, query) {
    return this.apiRequest('PUT', 'push_audience', { name, query }, { useMasterKey: true });
  }

  deletePushAudience(objectId) {
    return this.apiRequest('DELETE', 'push_audience', { objectId }, { useMasterKey: true });
  }

  fetchPushNotifications(type, page) {
    const params = { order: 'createdAt' };

    if (page) {
      params.page = page;
    }

    return this.apiRequest('GET', 'push', params, { useMasterKey: true });
  }

  fetchPushAudienceSizeSuggestion() {
    const path = `/products/apps/${this.slug}/push_notifications/audience_size_suggestion`;
    return AJAX.get(path);
  }

  fetchPushDetails(objectId) {
    const query = new Parse.Query('_PushStatus');
    query.equalTo('objectId', objectId);

    return query.first({ useMasterKey: true });
  }

  fetchPushCount() {
    const query = new Parse.Query('_PushStatus');
    return query.count({ useMasterKey: true });
  }

  isLocalizationAvailable() {
    return this.serverInfo.features ? !!this.serverInfo.features.push.localization : null;
  }

  fetchPushLocales() {
    return this.supportedPushLocales;
  }

  fetchPushLocaleDeviceCount(audienceId, where, locales) {
    let path = `/products/apps/${this.slug}/push_subscriber_translation_count`;
    let urlsSeparator = '?';
    path += `?where=${encodeURI(JSON.stringify(where || {}))}`;
    path += `&locales=${encodeURI(JSON.stringify(locales))}`;
    urlsSeparator = '&';
    return AJAX.abortableGet(audienceId ? `${path}${urlsSeparator}audienceId=${audienceId}` : path);
  }

  fetchAvailableDevices() {
    return this.apiRequest('GET', 'available_devices', {}, { useMasterKey: true });
  }

  addCollaborator(email) {
    return SashiDo.addCollaborator({ email, id: this.id });
  }

  exportClass(className, where) {
    if (!where) {
      where = {};
    }

    const path = `/products/apps/${this.slug}/export_data`;
    return AJAX.put(path, { name: className, where });
  }

  getExportProgress() {
    const path = `/products/apps/${this.slug}/export_progress`;
    return AJAX.get(path);
  }

  getAvailableJobs() {
    return this.apiRequest('GET', 'job/available', {}, { useMasterKey: true });
  }

  getJobStatus() {
    if (new Date() - this.jobStatus.lastFetched < 60000) {
      return Promise.resolve(this.jobStatus.status);
    }

    return this.apiRequest('GET', 'job/status', {}, { useMasterKey: true }).then((status) => {
      this.jobStatus = {
        status: status || null,
        lastFetched: new Date(),
      };

      return Promise.resolve(status);
    });
  }

  runJob(job) {
    const endpoint = `jobs/${job.jobName}`;

    const data = {
      description: 'Executing from job schedule web console.',
      input: JSON.parse(job.params || '{}'),
      jobName: job.jobName,
      when: 0,
    };

    return this.apiRequest('POST', endpoint, data, { useMasterKey: true });
  }

  migration_server = async () => {
    const promise = new Promise((resolve) => {
      const timer = setInterval(() => {
        this.getServerInfo()
          .then(() => {
            clearInterval(timer);
            resolve();
          });
      }, 1000)
    });

    return promise;
  }
}
